import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Inject,
  Input,
  OnInit,
  Output,
  PLATFORM_ID,
  QueryList,
  Renderer2,
  ViewChildren,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { IPicture } from '@api/models/image/picture';
import { IMAGE_RESOLUTION } from '@api/models/image/image-resolution';
import { IVideo } from '@api/models/video/video';
import { LibVideoPlayerService } from '@components/video-player/services/lib-video-player.service';
import { ImageSubType } from '@api/models/image/image-type';
import { UrlService } from '@utils/services/url/url.service';
import { AnalyticsService } from '@utils/services/analytics/analytics.service';
import {SubscriptionTrackingInfo} from '@lib/models/subscription/subscription-tracking-info';

interface ICarouselMediaObject {
  image?: IPicture;
  video?: IVideo;
  youtubeVideo?: SafeResourceUrl;
  position?: 'top' | 'center';
  fit?: 'contain' | 'cover';
}

@Component({
  selector: 'lib-image-carousel',
  templateUrl: './image-carousel.component.html',
  styleUrls: ['./image-carousel.component.scss'],
})
export class ImageCarouselComponent implements OnInit {
  public currentImageCredit = '';
  public currentImageTitle = '';
  public mediaList: ICarouselMediaObject[] = [];
  public hasVideo = false;
  @HostBinding('class.landscape') public landscape = false;
  @HostBinding('style.paddingBottom') public aspectRatio = '0';
  @Input() public preloadFirstImage = false;
  @Input() public firstImageAsVideoPreview = false;
  @Input() public lazyLoadImages = false;
  @Input() public paywall = false;
  @Input() public trackingInfo: SubscriptionTrackingInfo = null;
  @Input() public showAds = true;
  @Input() public showCredit = true;
  @Input() public showAnchors = true;
  @Input() public showCounter = true;
  @Input() public autoStartVideo = false;
  @Input() public user: any;
  @Input() public currentSlideIndex = 0;
  @Input() public images: IPicture[] = [];
  @Input() public videos: IVideo[] = [];
  @Input() public youtubeVideo: string;
  @Input() public creditsBottomSpace = '-2px';
  @Input() public targetRes: IMAGE_RESOLUTION;
  @Input() @HostBinding('class.full-screen') public fullScreen = false;
  @Output() public imageClick = new EventEmitter<number>();
  @Output() public imageChange = new EventEmitter<number>();
  @ViewChildren('slide') private slides: QueryList<ElementRef>;

  constructor(
    private domSanitizer: DomSanitizer,
    private analyticsService: AnalyticsService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  public ngOnInit() {
    if (this.fullScreen) {
      this.targetRes = IMAGE_RESOLUTION.HIGH;
    }
    this.createMediaList();
    this.setCredit();
  }

  public getSlideStyle(slideIndex: number): string {
    const diff = slideIndex - this.currentSlideIndex;
    if (Math.abs(diff) > 1) {
      return `transform: translateX(${100 * Math.sign(diff)}%)`;
    }
    return `transform: translateX(${100 * diff}%)`;
  }

  public prevImage(ev?: Event, emitChange = true): void {
    ev?.stopPropagation();
    ev?.preventDefault();
    if (this.currentSlideIndex > 0) {
      this.changeImage(this.currentSlideIndex - 1);
      if (emitChange) {
        this.imageChange.emit(this.currentSlideIndex - 1);
      }
    }
  }

  public nextImage(ev?: Event, emitChange = true): void {
    ev?.stopPropagation();
    ev?.preventDefault();
    if (this.currentSlideIndex < this.slides.length - 1) {
      this.changeImage(this.currentSlideIndex + 1);
      if (emitChange) {
        this.imageChange.emit(this.currentSlideIndex + 1);
      }
    }
  }

  public onImageClick(index: number, event: Event): void {
    this.imageClick.emit(this.hasVideo ? index - 1 : index);
  }

  public isCounterHidden(): boolean {
    if (this.mediaList.length === 1 || !this.showCounter) {
      return false;
    }
    return !(this.hasVideo && this.currentSlideIndex === 0);
  }

  public changeImage(index: number): void {
    this.analyticsService.sendPageView();
    this.currentSlideIndex = index;
    if (this.mediaList?.length === 0) {
      this.createMediaList();
      return;
    }
    if (!this.mediaList[index]) {
      return;
    }
    this.setCredit();
  }

  private setCredit(): void {
    if (!this.mediaList[this.currentSlideIndex]) {
      return;
    }
    this.currentImageCredit =
      this.mediaList[this.currentSlideIndex].image?.source ?? null;
    this.currentImageTitle =
      this.mediaList[this.currentSlideIndex].image?.text ?? null;
  }

  private createMediaList(): void {
    if (this.mediaList.length > 0) {
      return;
    }
    if (this.videos?.length > 0) {
      let liveStream: IVideo;
      this.videos.forEach((video) => {
        if (video.liveStream) {
          liveStream = video;
        }
        this.prepareVideoPreview(video);
      });
      this.hasVideo = true;
      this.landscape = true;
      // If a livestream is active do not add other media than the live stream
      // No carousel should be shown
      if (liveStream) {
        this.mediaList.push({ video: liveStream });
        return;
      }
      this.mediaList.push(
        ...this.videos.map((video) => ({ video }) as ICarouselMediaObject)
      );
    }
    const firstImageAsPreview =
      this.firstImageAsVideoPreview && this.images.length;
    if (this.youtubeVideo?.length && !firstImageAsPreview) {
      this.youtubeVideo = UrlService.convertYtUrlToEmbedUrl(this.youtubeVideo);
      if (this.youtubeVideo) {
        const autoPlay = this.autoStartVideo ? '&autoplay=1&mute=1' : '';
        this.youtubeVideo +=
          '&enablejsapi=1&version=3&playerapiid=ytplayer' + autoPlay;
        this.mediaList.push({
          youtubeVideo: this.domSanitizer.bypassSecurityTrustResourceUrl(
            this.youtubeVideo
          ),
        });
        this.hasVideo = true;
        this.landscape = true;
        return;
      }
    }
    let fit: 'contain' | 'cover' = this.fullScreen ? 'contain' : 'cover';
    let firstImageAspectRatio = 0.5625; // 16:9 as default
    // If first image is landscape the slider is also set to landscape
    // All following images that are not landscape are set to contain
    this.images.forEach((image, index) => {
      if (!this.fullScreen) {
        const file = image.resized_files[0];
        const aspectRatio = file.height / file.width;
        if (index === 0 && !this.landscape) {
          firstImageAspectRatio = aspectRatio;
          this.aspectRatio = aspectRatio < 1 ? `${aspectRatio * 100}%` : '100%';
        } else {
          fit = aspectRatio > firstImageAspectRatio ? 'contain' : 'cover';
        }
      }
      this.mediaList.push({ image, position: 'center', fit });
    });
  }

  private prepareVideoPreview(video: IVideo): void {
    const picture = {
      id: null,
      subtype: ImageSubType.IMAGE,
      extension: null,
      text: null,
      source: null,
      resized_files: null,
    };
    if (!video.preview && video.preview_url?.length) {
      picture.resized_files = [
        {
          width: 1920,
          height: 1080,
          url: video.preview_url,
        },
      ];
      video.preview = picture;
    }
    // Use first image as preview
    if (!video.preview && this.images?.length) {
      video.preview = this.images.splice(0, 1)[0];
    }
    // If video still has no preview use default fan.at preview
    // Lazy image will show placeholder if url is null
    if (!video.preview) {
      picture.resized_files = [
        {
          width: 1920,
          height: 1080,
          url: null,
        },
      ];
      video.preview = picture;
    }
  }
}
